import {
  ApiError,
  type ChannelAlias,
  type SpuDetail,
  type SpuDetailSku,
  useAddCart,
  useAddFavorite,
  useBuyNow,
  useDeleteFavorites,
  useProductDetail,
} from "@kupikupi/api";
import { FREE_INSPECTION_PHOTO_COUNT_LABEL, FREE_STORAGE_DAYS } from "@kupikupi/brand";
import { formatPrice } from "@kupikupi/money";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TgEmpty } from "../components/TgEmpty";
import { TgHeader } from "../components/TgHeader";
import { TgPage } from "../components/TgPage";
import { TgSkeleton } from "../components/TgSkeleton";
import { useAuth, useAuthToken } from "../lib/auth";

const TILE_BG = "linear-gradient(135deg,#FFE5DD,#FFD3C2)";

/**
 * Stable key for a SKU attribute — uses display `name` first (Weidian
 * per-value codes differ per SKU; 1688 codes are stable per attr type).
 */
function attrKey(a: { code?: string | null; name?: string | null }): string {
  return (a.name || a.code || "").trim();
}

interface AttrGroup {
  key: string;
  name: string;
  values: { enumValue: string; disabled: boolean }[];
}

/**
 * Group SKU attrs by name; cross-filter: picking a value in group A disables
 * values in group B that have no in-stock SKU matching the combo.
 */
function groupSkuAttrs(detail: SpuDetail, selected: Record<string, string>): AttrGroup[] {
  const keys = Array.from(
    new Set(detail.skuList.flatMap((s) => s.attrs.map(attrKey))),
  ).filter(Boolean);

  const allAttrs = detail.skuList.map((sku) => ({
    stock: sku.stock ?? 0,
    map: new Map(sku.attrs.map((a) => [attrKey(a), { value: a.enumValue, name: a.name }])),
  }));

  return keys.map((key) => {
    const displayName = allAttrs.find((m) => m.map.has(key))?.map.get(key)?.name ?? key;
    const values = new Map<string, { disabled: boolean }>();
    for (const { stock, map } of allAttrs) {
      const val = map.get(key);
      if (!val?.value) continue;
      let reachable = stock > 0;
      if (reachable) {
        for (const [otherKey, otherVal] of Object.entries(selected)) {
          if (otherKey === key) continue;
          const skuVal = map.get(otherKey);
          if (!skuVal || skuVal.value !== otherVal) {
            reachable = false;
            break;
          }
        }
      }
      const existing = values.get(val.value);
      values.set(val.value, {
        disabled: existing ? existing.disabled && !reachable : !reachable,
      });
    }
    return {
      key,
      name: displayName,
      values: Array.from(values.entries()).map(([enumValue, meta]) => ({
        enumValue,
        disabled: meta.disabled,
      })),
    };
  });
}

/**
 * Product detail · 1:1 port of `mini-product.html` — full-bleed hero,
 * discount badge, SKU selector (color+size linked, OOS disabled+strikethrough),
 * price block with strike-through original + savings, sticky bottom CTA bar,
 * favorite toggle, add-to-cart + buy-now wired to real API.
 */
export function ProductPage() {
  const { t } = useTranslation("product");
  const params = useParams({ strict: false }) as {
    channel?: ChannelAlias;
    spuNo?: string;
  };
  const channel = params.channel as ChannelAlias;
  const spuNo = params.spuNo ?? "";

  const auth = useAuth();
  const authPresent = auth.ready ? !!auth.token : undefined;

  const detailQ = useProductDetail(
    { channel, spuNo },
    { ...(authPresent !== undefined ? { authPresent } : {}) },
  );

  const [imgIdx, setImgIdx] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const [selected, setSelected] = useState<Record<string, string>>({});

  // Favorite local state — seeded from detail.favorite after data arrives.
  const [faved, setFaved] = useState<boolean | null>(null);

  const authToken = useAuthToken();
  const addCart = useAddCart(authToken);
  const buyNowMut = useBuyNow(authToken);
  const addFav = useAddFavorite(authToken);
  const delFav = useDeleteFavorites(authToken);
  const navigate = useNavigate();

  const detail = detailQ.data;

  // Seed faved from detail once loaded (only once per product).
  // biome-ignore lint/correctness/useExhaustiveDependencies: seed once
  useEffect(() => {
    if (detail && faved === null) {
      setFaved(detail.favorite === true);
    }
  }, [detail?.spuNo]);

  // Seed SKU selection defaults when detail loads.
  // biome-ignore lint/correctness/useExhaustiveDependencies: seed once
  useEffect(() => {
    if (!detail) return;
    const groups = groupSkuAttrs(detail, {});
    setSelected((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const g of groups) {
        if (!next[g.key] || !g.values.find((v) => v.enumValue === next[g.key] && !v.disabled)) {
          const first = g.values.find((v) => !v.disabled) ?? g.values[0];
          if (first) {
            next[g.key] = first.enumValue;
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [detail?.spuNo]);

  const attrGroups = useMemo(
    () => (detail ? groupSkuAttrs(detail, selected) : []),
    [detail, selected],
  );

  // Active SKU = full attribute match
  const activeSku: SpuDetailSku | null = useMemo(() => {
    if (!detail) return null;
    if (Object.keys(selected).length === 0) {
      return detail.skuList.find((s) => (s.stock ?? 0) > 0) ?? detail.skuList[0] ?? null;
    }
    for (const sku of detail.skuList) {
      let match = true;
      for (const a of sku.attrs) {
        if (selected[attrKey(a)] !== a.enumValue) {
          match = false;
          break;
        }
      }
      if (match && sku.attrs.length > 0) return sku;
    }
    return null;
  }, [detail, selected]);

  const isNoVariant = (detail?.skuList.length ?? 0) === 0;
  const canAddCart = isNoVariant || activeSku !== null;

  const unitPrice = activeSku?.priceRubKopecks ?? detail?.priceRubKopecks ?? 0;
  const unitOriginal = activeSku?.originalPriceRubKopecks ?? detail?.originalPriceRubKopecks ?? null;
  const discount =
    unitOriginal && unitOriginal > unitPrice
      ? Math.round(((unitOriginal - unitPrice) / unitOriginal) * 100)
      : null;

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  }, []);

  const handleAddCart = useCallback(async () => {
    if (!detail) return;
    if (!authToken) {
      showToast(t("buy.fav_signin_required"));
      return;
    }
    try {
      await addCart.mutateAsync({
        channel: detail.channel,
        spuNo: detail.spuNo,
        skuNo: activeSku?.skuNo ?? "",
        quantity: 1,
        subject: detail.title,
        ...(detail.imageUrl ? { mainImg: detail.imageUrl } : {}),
        ...(detail.detailUrl ? { detailUrl: detail.detailUrl } : {}),
        ...(activeSku ? { skuRemarks: activeSku.attrs.map((a) => a.enumValue).join(" ") } : {}),
      });
      showToast(t("toast.added_to_cart"));
    } catch {
      showToast(t("buy.fav_failed"));
    }
  }, [detail, activeSku, authToken, addCart, showToast, t]);

  const handleBuyNow = useCallback(async () => {
    if (!detail) return;
    if (!authToken) {
      showToast(t("buy.fav_signin_required"));
      return;
    }
    try {
      await buyNowMut.mutateAsync({
        channel: detail.channel,
        spuNo: detail.spuNo,
        skuNo: activeSku?.skuNo ?? "",
        quantity: 1,
        subject: detail.title,
        ...(detail.imageUrl ? { mainImg: detail.imageUrl } : {}),
        ...(detail.detailUrl ? { detailUrl: detail.detailUrl } : {}),
        ...(activeSku ? { skuRemarks: activeSku.attrs.map((a) => a.enumValue).join(" ") } : {}),
      });
      navigate({ to: "/cart" as never });
    } catch {
      showToast(t("buy.fav_failed"));
    }
  }, [detail, activeSku, authToken, buyNowMut, navigate, showToast, t]);

  const handleToggleFav = useCallback(async () => {
    if (!detail) return;
    if (!authToken) {
      showToast(t("buy.fav_signin_required"));
      return;
    }
    const wasFaved = faved ?? false;
    // Optimistic update
    setFaved(!wasFaved);
    try {
      if (wasFaved) {
        await delFav.mutateAsync({ type: "spu", spuNos: [detail.spuNo] });
        showToast(t("buy.fav_removed"));
      } else {
        await addFav.mutateAsync({
          type: "spu",
          channel: detail.channel,
          spuNo: detail.spuNo,
          subject: detail.title,
          mainImage: detail.imageUrl,
          detailUrl: detail.detailUrl,
        });
        showToast(t("buy.fav_added"));
      }
    } catch {
      // Rollback
      setFaved(wasFaved);
      showToast(t("buy.fav_failed"));
    }
  }, [detail, authToken, faved, addFav, delFav, showToast, t]);

  // ── Error / loading states ──────────────────────────────────────────────
  if (!detail) {
    if (detailQ.isError) {
      const isOos =
        detailQ.error instanceof ApiError && detailQ.error.code === "1011";
      return (
        <TgPage tabbar={false}>
          <TgHeader title={t("head.title")} back />
          <div className="tg-body" style={{ flex: 1 }}>
            <TgEmpty
              icon={isOos ? "📦" : "⚠️"}
              title={isOos ? t("state.oos_title") : t("state.error_title")}
              hint={isOos ? t("state.oos_hint") : t("state.error_hint")}
              cta={
                <Link
                  to={"/catalog" as never}
                  style={{
                    display: "inline-block",
                    padding: "8px 18px",
                    background: "var(--color-cta)",
                    color: "#fff",
                    borderRadius: 999,
                    fontWeight: 800,
                    fontSize: 12,
                    textDecoration: "none",
                  }}
                >
                  {t("error.out_of_stock_cta")}
                </Link>
              }
            />
          </div>
        </TgPage>
      );
    }
    // Loading skeleton
    return (
      <TgPage tabbar={false}>
        <TgHeader title={t("head.title")} back />
        {/* Hero skeleton */}
        <div
          style={{
            aspectRatio: "1",
            background: TILE_BG,
          }}
        />
        <div className="tg-body" style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>
          <TgSkeleton height={22} width="90%" />
          <TgSkeleton height={18} width="50%" />
          <TgSkeleton height={72} radius={12} />
          <TgSkeleton height={80} radius={10} />
          <TgSkeleton height={56} radius={10} />
        </div>
        <div
          style={{
            position: "sticky",
            bottom: 0,
            background: "#fff",
            borderTop: "1px solid var(--color-border)",
            padding: "8px 12px",
            display: "flex",
            gap: 8,
            alignItems: "center",
          }}
        >
          <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--color-surface)" }} />
          <TgSkeleton height={40} radius={999} />
          <TgSkeleton height={40} radius={999} />
        </div>
      </TgPage>
    );
  }

  const images: string[] = [detail.imageUrl, ...(detail.imageList ?? [])].filter(
    (u): u is string => !!u,
  );
  const hero = images[imgIdx] ?? images[0];

  return (
    <TgPage tabbar={false}>
      <TgHeader
        title={t("head.title")}
        back
        right={
          <button
            type="button"
            onClick={handleToggleFav}
            aria-pressed={faved ?? false}
            style={{
              background: "none",
              border: 0,
              padding: 0,
              fontSize: 20,
              cursor: "pointer",
              lineHeight: 1,
              color: faved ? "var(--color-price)" : "var(--color-text-muted)",
            }}
          >
            {faved ? "❤️" : "🤍"}
          </button>
        }
      />

      {/* ── Hero image ─────────────────────────────────────────────────── */}
      <div
        style={{
          aspectRatio: "1",
          background: TILE_BG,
          display: "grid",
          placeItems: "center",
          fontSize: 160,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {hero ? (
          <img
            src={hero}
            alt={detail.title}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          "🛍️"
        )}

        {/* Discount badge */}
        {discount !== null && discount > 0 && (
          <span
            style={{
              position: "absolute",
              top: 12,
              left: 12,
              background: "var(--color-price)",
              color: "#fff",
              fontSize: 11,
              fontWeight: 900,
              padding: "3px 10px",
              borderRadius: 4,
              transform: "rotate(-4deg)",
            }}
          >
            −{discount}% OFF
          </span>
        )}

        {/* Channel badge */}
        {channel && (
          <span
            style={{
              position: "absolute",
              bottom: 12,
              left: 12,
              background: "rgba(15,31,26,.7)",
              color: "#fff",
              fontSize: 11,
              fontWeight: 700,
              padding: "3px 10px",
              borderRadius: 999,
            }}
          >
            {channel}
          </span>
        )}

        {/* Image counter */}
        <span
          style={{
            position: "absolute",
            bottom: 12,
            right: 12,
            background: "rgba(15,31,26,.7)",
            color: "#fff",
            fontSize: 11,
            fontFamily: "monospace",
            padding: "3px 10px",
            borderRadius: 999,
          }}
        >
          {imgIdx + 1} / {Math.max(images.length, 1)}
        </span>
      </div>

      {/* ── Image thumbnails ────────────────────────────────────────────── */}
      {images.length > 1 && (
        <div
          style={{
            display: "flex",
            gap: 6,
            padding: "8px 12px",
            overflowX: "auto",
            background: "#fff",
            borderBottom: "1px solid var(--color-border)",
          }}
        >
          {images.map((url, i) => (
            <button
              key={url}
              type="button"
              onClick={() => setImgIdx(i)}
              style={{
                flexShrink: 0,
                width: 44,
                height: 44,
                borderRadius: 6,
                overflow: "hidden",
                padding: 0,
                background: "transparent",
                border:
                  imgIdx === i
                    ? "2px solid var(--color-primary-fill)"
                    : "1px solid var(--color-border)",
              }}
            >
              <img
                src={url}
                alt={`${i + 1}`}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </button>
          ))}
        </div>
      )}

      <div className="tg-body" style={{ flex: 1 }}>
        {/* ── Title ────────────────────────────────────────────────────── */}
        <h1
          style={{
            fontSize: 18,
            fontWeight: 800,
            lineHeight: 1.3,
            margin: 0,
          }}
        >
          {detail.title}
        </h1>

        {/* ── Price block ──────────────────────────────────────────────── */}
        <div
          style={{
            background: "linear-gradient(135deg,#FFF0EB,#FFE4D9)",
            borderRadius: 12,
            padding: "12px 14px",
            display: "flex",
            alignItems: "baseline",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 26,
              fontWeight: 900,
              color: "var(--color-price)",
            }}
          >
            {formatPrice(unitPrice, "ru-RU", "RUB")}
          </span>
          {detail.cnyFen > 0 && (
            <span
              style={{
                fontSize: 11,
                color: "var(--color-text-muted)",
                fontFamily: "monospace",
              }}
            >
              ≈ {formatPrice(detail.cnyFen, "zh-CN", "CNY")}
            </span>
          )}
          {unitOriginal && unitOriginal > unitPrice && (
            <span
              style={{
                fontSize: 11,
                color: "var(--color-price-strike, #aaa)",
                textDecoration: "line-through",
                fontFamily: "monospace",
              }}
            >
              {formatPrice(unitOriginal, "ru-RU", "RUB")}
            </span>
          )}
          {unitOriginal && unitOriginal > unitPrice && (
            <span
              style={{
                marginLeft: "auto",
                background: "var(--color-cta)",
                color: "#fff",
                fontSize: 10,
                fontWeight: 900,
                padding: "2px 7px",
                borderRadius: 4,
              }}
            >
              {t("mini.save_label", {
                amount: formatPrice(unitOriginal - unitPrice, "ru-RU", "RUB"),
              })}
            </span>
          )}
        </div>

        {/* ── SKU selector ─────────────────────────────────────────────── */}
        {attrGroups.map((group) => {
          const selectedVal = selected[group.key];
          return (
            <div key={group.key} className="tg-card">
              <h4 className="tg-card__h">
                {group.name}
                {selectedVal ? ` · ${selectedVal}` : ""}
              </h4>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {group.values.map((v) => {
                  const isSelected = selectedVal === v.enumValue;
                  return (
                    <button
                      key={v.enumValue}
                      type="button"
                      disabled={v.disabled}
                      onClick={() =>
                        setSelected((prev) => ({ ...prev, [group.key]: v.enumValue }))
                      }
                      style={{
                        background: isSelected ? "var(--color-text)" : "#fff",
                        color: v.disabled
                          ? "var(--color-text-disabled, #bbb)"
                          : isSelected
                            ? "#fff"
                            : "var(--color-text)",
                        padding: "5px 10px",
                        borderRadius: 6,
                        fontSize: 11,
                        fontWeight: 700,
                        border: isSelected
                          ? "1.5px solid var(--color-text)"
                          : "1.5px solid var(--color-border)",
                        textDecoration: v.disabled ? "line-through" : "none",
                        cursor: v.disabled ? "not-allowed" : "pointer",
                        opacity: v.disabled ? 0.6 : 1,
                      }}
                    >
                      {v.enumValue}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* ── Shipping ─────────────────────────────────────────────────── */}
        <div className="tg-card">
          <h4 className="tg-card__h">{t("mini.shipping_title")}</h4>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr auto",
              gap: 8,
              padding: 8,
              background: "var(--color-primary-soft)",
              border: "1px solid var(--color-primary-fill)",
              borderRadius: 8,
              alignItems: "center",
              fontSize: 11,
            }}
          >
            <span style={{ fontSize: 16 }}>✈</span>
            <div>
              <strong>{t("mini.shipping_line")}</strong>
              <div
                style={{
                  color: "var(--color-text-muted)",
                  fontSize: 10,
                  marginTop: 1,
                }}
              >
                {t("mini.shipping_eta")}
              </div>
            </div>
            <span
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 900,
                color: "var(--color-price)",
              }}
            >
              + 890 ₽
            </span>
          </div>
        </div>

        {/* ── Trust grid ───────────────────────────────────────────────── */}
        <div className="tg-card">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2,1fr)",
              gap: 8,
              fontSize: 11,
              color: "var(--color-text-muted)",
            }}
          >
            <div>✓ {t("mini.trust_photos", { photoCount: FREE_INSPECTION_PHOTO_COUNT_LABEL })}</div>
            <div>✓ {t("mini.trust_replace")}</div>
            <div>✓ {t("mini.trust_storage", { days: FREE_STORAGE_DAYS })}</div>
            <div>✓ {t("mini.trust_support")}</div>
          </div>
        </div>

        {/* ── Source details note ──────────────────────────────────────── */}
        <div
          style={{
            fontSize: 11,
            color: "var(--color-text-muted)",
            padding: 12,
            background: "var(--color-surface)",
            borderRadius: 10,
            lineHeight: 1.55,
          }}
        >
          <strong style={{ color: "var(--color-text)" }}>{t("mini.source_details_title")}</strong>
          <br />
          {t("mini.source_details_body")}
          {detail.detailUrl && (
            <>
              {" "}
              <a
                href={detail.detailUrl}
                target="_blank"
                rel="noreferrer"
                style={{
                  color: "var(--color-primary-fill)",
                  fontWeight: 700,
                }}
              >
                {t("mini.open_source", { channel })}
              </a>
            </>
          )}
        </div>
      </div>

      {/* ── Sticky bottom action bar ──────────────────────────────────── */}
      <div
        style={{
          position: "sticky",
          bottom: 0,
          background: "#fff",
          borderTop: "1px solid var(--color-border)",
          padding: "8px 12px",
          display: "flex",
          gap: 8,
          alignItems: "center",
          zIndex: 10,
        }}
      >
        {/* Favorite toggle */}
        <button
          type="button"
          onClick={handleToggleFav}
          aria-pressed={faved ?? false}
          style={{
            width: 40,
            height: 40,
            background: "var(--color-surface)",
            border: 0,
            borderRadius: "50%",
            fontSize: 18,
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          {faved ? "❤️" : "🤍"}
        </button>

        {/* Add to cart */}
        <button
          type="button"
          onClick={handleAddCart}
          disabled={!canAddCart || addCart.isPending}
          style={{
            flex: 1,
            background: "#fff",
            color: "var(--color-text)",
            border: "1.5px solid var(--color-border-strong)",
            padding: 11,
            borderRadius: 999,
            fontWeight: 800,
            fontSize: 13,
            textAlign: "center",
            cursor: canAddCart ? "pointer" : "not-allowed",
            opacity: !canAddCart || addCart.isPending ? 0.6 : 1,
          }}
        >
          {addCart.isPending ? t("buy.cta_buy_loading") : t("mobile_cta.cart_short")}
        </button>

        {/* Buy now */}
        <button
          type="button"
          onClick={handleBuyNow}
          disabled={!canAddCart || buyNowMut.isPending}
          style={{
            flex: 1,
            background: "var(--color-cta)",
            color: "#fff",
            padding: 11,
            borderRadius: 999,
            fontWeight: 800,
            fontSize: 13,
            textAlign: "center",
            cursor: canAddCart ? "pointer" : "not-allowed",
            opacity: !canAddCart || buyNowMut.isPending ? 0.6 : 1,
            border: 0,
          }}
        >
          {buyNowMut.isPending ? t("buy.cta_buy_loading") : t("mobile_cta.buy_short")}
        </button>
      </div>

      {/* ── Toast ────────────────────────────────────────────────────────── */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: "fixed",
            left: "50%",
            bottom: 80,
            transform: "translateX(-50%)",
            background: "rgba(20,20,20,0.92)",
            color: "#fff",
            padding: "10px 18px",
            borderRadius: 999,
            fontSize: 13,
            fontWeight: 600,
            zIndex: 9000,
            boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
            whiteSpace: "nowrap",
          }}
        >
          {toast}
        </div>
      )}
    </TgPage>
  );
}
